/* eslint-disable no-underscore-dangle */
import 'es6-promise/auto';
import 'isomorphic-fetch';
import 'core-js/stable';

import { BrowserRouter } from 'react-router-dom';
import { GoogleMapsApiKeyProvider } from '@honda-canada/experience-api-client';
import { HelmetProvider } from 'react-helmet-async';
import { Provider as ReduxProvider } from 'react-redux';
import { store } from '@honda-canada/sitecore-jss-components/lib/core/store';
import AppRoot from '@honda-canada/sitecore-jss-common/lib/components/AppRoot';
import BottomElementProvider from '@honda-canada/sitecore-jss-components/lib/components/Footer/service/BottomElementProvider';
import ErrorBoundary from '@honda-canada/sitecore-jss-common/lib/components/ErrorBoundary';
import i18nInit from '@honda-canada/sitecore-jss-common/lib/utils/i18nInit';
import isBrowserModern from '@honda-canada/sitecore-jss-common/lib/utils/isBrowserModern';
import LocalStorageProvider from '@honda-canada/sitecore-jss-components/lib/components/LocalStorageContext';
import ModelExplorationContextProvider from '@honda-canada/sitecore-jss-components/lib/components/ModelExplorationContext';
import ModelFiltersContextProvider from '@honda-canada/sitecore-jss-components/lib/components/ModelFiltersContext';
import PopupContextProvider from '@honda-canada/sitecore-jss-components/lib/components/Popup/PopupContext';
import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import ReducersProvider from '@honda-canada/sitecore-jss-components/lib/components/ReducersProvider';
import UserLocationProvider from '@honda-canada/user-location';
import { PSPTheme as Theme } from '@honda-canada/design-system-react/lib/psp-theme';
import TagManager from 'react-gtm-module';
import componentFactory from './componentFactory';
import ApplicationErrorMessage from './ApplicationErrorMessage';
import '@honda-canada/design-system-react/lib/psp-css.css';
import '@splidejs/splide/dist/css/themes/splide-default.min.css';

let renderFunction = ReactDOM.render;

let initLanguage = 'en';

let __JSS_STATE__ = null;
const ssrRawJson = document.getElementById('__JSS_STATE__');

if (ssrRawJson) {
  __JSS_STATE__ = JSON.parse(ssrRawJson.innerHTML);
}

if (__JSS_STATE__) {
  renderFunction = ReactDOM.hydrate;
  initLanguage = __JSS_STATE__.sitecore.context.language;
}

const { config } = __JSS_STATE__;

const gtmDevParameters = new URLSearchParams(config.gtmDevParameters);
const tagManagerArgs = config.gtmDevParameters
  ? {
      gtmId: 'GTM-W6D96H2',
      auth: gtmDevParameters && gtmDevParameters.get('gtm_auth'),
      preview: gtmDevParameters && gtmDevParameters.get('gtm_preview'),
    }
  : {
      gtmId: 'GTM-W6D96H2',
    };

TagManager.initialize(tagManagerArgs);

const App = () => {
  const googleMapsConfig = {
    env: config?.hondaExperienceApiEnv,
    consumerId: config?.hondaExperienceApiConsumerId,
    consumerName: config?.hondaExperienceApiConsumerName,
  };
  const renderLegacyHeaderAndFooterFeatureFlag = __JSS_STATE__?.viewBag?.featureFlags?.legacyHeaderAndFooter;
  const [reloadLocationFromCookies, setReloadLocationFromCookies] = useState(false);

  return (
    <ErrorBoundary errorMessage={<ApplicationErrorMessage />}>
      <ReduxProvider store={store}>
        <HelmetProvider>
          <GoogleMapsApiKeyProvider {...googleMapsConfig}>
            {googleMapsApiKey => (
              <UserLocationProvider
                googleMapsApiKey={googleMapsApiKey}
                forceReload={reloadLocationFromCookies}
                onLocationChanged={() => {
                  setReloadLocationFromCookies(!reloadLocationFromCookies);
                }}
              >
                <LocalStorageProvider>
                  <AppRoot
                    activePlaceholders={
                      renderLegacyHeaderAndFooterFeatureFlag ? ['main'] : ['header', 'main', 'footer']
                    }
                    bottomElementProvider={BottomElementProvider}
                    componentFactory={componentFactory}
                    config={config}
                    errorMessage={<ApplicationErrorMessage is404 />}
                    modelExplorationContextProvider={ModelExplorationContextProvider}
                    modelFiltersContextProvider={ModelFiltersContextProvider}
                    path={window.location.pathname}
                    popupContextProvider={PopupContextProvider}
                    ReducersProvider={ReducersProvider}
                    Router={BrowserRouter}
                    ssrState={__JSS_STATE__}
                    theme={Theme}
                  />
                </LocalStorageProvider>
              </UserLocationProvider>
            )}
          </GoogleMapsApiKeyProvider>
        </HelmetProvider>
      </ReduxProvider>
    </ErrorBoundary>
  );
};

const renderApp = async () => {
  const dictionary = __JSS_STATE__?.viewBag?.dictionary;
  const rootElement = document.getElementById('jss-root');

  await i18nInit(initLanguage, dictionary, config);

  renderFunction(<App />, rootElement);
};

if (isBrowserModern()) {
  renderApp();
} else {
  import('./polyfills').then(renderApp);
}
